import { useMemo, useState } from 'react'
import { addDays, mondayOf } from './dates.ts'

export type SlidePhase = 'idle' | 'next' | 'prev'

// The 8-day window every day-column shows — Monday of the current week
// through the following Monday — plus the prev/next slide between windows.
// Shared by the planner and the recipe picker so both navigate weeks the
// same way.
export const useWeekWindow = () => {
	const [weekStart, setWeekStart] = useState(() => mondayOf(new Date()))
	const [phase, setPhase] = useState<SlidePhase>('idle')

	// Dates rendered inside the column. During a slide we render 15 rows so
	// both the old and new windows are mounted; the keyframe animation slides
	// the column to expose the new 8-row window.
	const dates = useMemo(() => {
		if (phase === 'next') {
			return Array.from({ length: 15 }, (_, i) => addDays(weekStart, i))
		}
		if (phase === 'prev') {
			return Array.from({ length: 15 }, (_, i) =>
				addDays(weekStart, i - 7),
			)
		}
		return Array.from({ length: 8 }, (_, i) => addDays(weekStart, i))
	}, [weekStart, phase])

	const slide = (dir: 'next' | 'prev') => {
		if (phase !== 'idle') return
		setPhase(dir)
	}

	const onSlideEnd = () => {
		setWeekStart((w) =>
			addDays(w, phase === 'next' ? 7 : phase === 'prev' ? -7 : 0),
		)
		setPhase('idle')
	}

	return {
		weekStart,
		dates,
		phase,
		sliding: phase !== 'idle',
		slide,
		onSlideEnd,
	}
}
